import type { Vec2 } from 'shared-types';

/** The 2D canvas camera: the world point at the screen center and pixels per meter. */
export interface Viewport {
  cu: number;
  cn: number;
  scale: number;
  /**
   * Turned 180°: the back (−n, the wall side) at the top and +u to the left, as seen standing in front of the object
   * facing the wall. A turn, not a mirror: the drawing reads as placed in a project. Doors / windows aren't turned.
   */
  turned?: boolean;
}

/** Whether the canvas shows this object turned (see Viewport.turned): objects are, doors / windows are not. */
export const isTurnedView = (spec: { opening?: unknown }): boolean => !spec.opening;

/** +1, or −1 when turned: world axes against screen axes. */
const sign = (v: Pick<Viewport, 'turned'>): number => (v.turned ? -1 : 1);

export interface CanvasSize {
  width: number;
  height: number;
}

export const MIN_SCALE = 20;
export const MAX_SCALE = 20000;

/** World (meters, +n up; turned: +n down, +u left) → screen pixels (+y down). */
export const toScreen = (v: Viewport, size: CanvasSize, p: Vec2): [number, number] => [size.width / 2 + sign(v) * (p[0] - v.cu) * v.scale, size.height / 2 - sign(v) * (p[1] - v.cn) * v.scale];

export const toWorld = (v: Viewport, size: CanvasSize, x: number, y: number): Vec2 => [v.cu + (sign(v) * (x - size.width / 2)) / v.scale, v.cn - (sign(v) * (y - size.height / 2)) / v.scale];

/** The world box the screen shows (its corners sorted, whichever way the view is turned). */
export const visibleWorld = (v: Viewport, size: CanvasSize): { min: Vec2; max: Vec2 } => {
  const a = toWorld(v, size, 0, 0);
  const b = toWorld(v, size, size.width, size.height);
  return { min: [Math.min(a[0], b[0]), Math.min(a[1], b[1])], max: [Math.max(a[0], b[0]), Math.max(a[1], b[1])] };
};

/** The view moved by a screen drag of (dx, dy) px from `start`: the world point under the pointer follows it. */
export const panBy = (start: Viewport, dx: number, dy: number): Viewport => ({ ...start, cu: start.cu - (sign(start) * dx) / start.scale, cn: start.cn + (sign(start) * dy) / start.scale });

/** Fit a `width` × `depth` footprint (centered on the origin) with room around it. */
export function fitViewport(size: CanvasSize, width: number, depth: number, turned = false, margin = 0.35): Viewport {
  const scale = Math.min(size.width / (width * (1 + 2 * margin)), size.height / (depth * (1 + 2 * margin)));
  return { cu: 0, cn: 0, scale: clampScale(Number.isFinite(scale) && scale > 0 ? scale : 200), turned };
}

export const clampScale = (scale: number): number => Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));

/** Zoom by `factor` keeping the world point under screen (x, y) fixed. */
export function zoomAt(v: Viewport, size: CanvasSize, x: number, y: number, factor: number): Viewport {
  const scale = clampScale(v.scale * factor);
  const anchor = toWorld(v, size, x, y);
  return { ...v, scale, cu: anchor[0] - (sign(v) * (x - size.width / 2)) / scale, cn: anchor[1] + (sign(v) * (y - size.height / 2)) / scale };
}

/**
 * Grid steps for the current zoom: the smallest of 1 cm / 5 cm / 10 cm /
 * 50 cm / 1 m / 5 m that is at least `minPx` pixels apart, and a major step
 * ten times (or five times for the 5-series) bigger.
 */
export function gridSteps(scale: number, minPx = 10): { minor: number; major: number } {
  const steps = [0.01, 0.05, 0.1, 0.5, 1, 5, 10];
  const minor = steps.find((s) => s * scale >= minPx) ?? 10;
  const major = steps.find((s) => s >= minor * 5 && s * scale >= minPx * 5) ?? minor * 10;
  return { minor, major };
}

/** "1.20 m" / "45 cm" style length label. */
export function formatLength(meters: number): string {
  return Math.abs(meters) >= 1 ? `${meters.toFixed(2)} m` : `${Math.round(meters * 1000) / 10} cm`;
}

/** SVG path of a polyline in screen space. */
export function polylinePath(points: readonly Vec2[], project: (p: Vec2) => [number, number]): string {
  return points.map((p, i) => `${i === 0 ? 'M' : 'L'}${project(p).map((c) => c.toFixed(1)).join(' ')}`).join(' ');
}

/** SVG path of flat `[x1,y1,z,x2,y2,z,…]` segments in screen space. */
export function segmentsPath(segments: readonly number[], project: (p: Vec2) => [number, number]): string {
  const parts: string[] = [];
  for (let i = 0; i + 5 < segments.length; i += 6) {
    const [ax, ay] = project([segments[i], segments[i + 1]]);
    const [bx, by] = project([segments[i + 3], segments[i + 4]]);
    parts.push(`M${ax.toFixed(1)} ${ay.toFixed(1)}L${bx.toFixed(1)} ${by.toFixed(1)}`);
  }
  return parts.join('');
}

/**
 * SVG path of flat segments with consecutive touching segments joined into
 * one polyline — so a stroke dash pattern runs on along a tessellated curve
 * instead of restarting at every chord.
 */
export function chainedSegmentsPath(segments: readonly number[], project: (p: Vec2) => [number, number]): string {
  const parts: string[] = [];
  let last: [number, number] | null = null;
  for (let i = 0; i + 5 < segments.length; i += 6) {
    const [ax, ay] = project([segments[i], segments[i + 1]]);
    const [bx, by] = project([segments[i + 3], segments[i + 4]]);
    // A new subpath unless this segment starts where the previous one ended (a path must open with M).
    if (!last || Math.abs(ax - last[0]) > 0.05 || Math.abs(ay - last[1]) > 0.05) parts.push(`M${ax.toFixed(1)} ${ay.toFixed(1)}`);
    parts.push(`L${bx.toFixed(1)} ${by.toFixed(1)}`);
    last = [bx, by];
  }
  return parts.join('');
}
